import {Injectable, OnDestroy} from '@angular/core';
import {lastValueFrom, Observable, Subscription, take} from "rxjs";
import {IdentityUser} from "@app/_models/identityUser";
import {
    AdminUser,
    BackoutRequest,
    BackoutResponse,
    CreateAdminUserRequest,
    CreateUserRequest,
    CreateWebDataConnectionSqlRequest,
    CreateWebDataConnectionThoughtSpotRequest,
    CreateMasterDataLoadResponse,
    Customer,
    DeleteUserRequest,
    DeleteWebDataConnectionRequest,
    DeleteWebDataConnectionResponse,
    Edi997Config,
    Edi997ConfigConnector,
    Edi997ConfigDetails,
    Edi997CreateRequest,
    ExtractConfigEntry,
    ExtractConfigEntryDetails,
    ExtractConfigEntryRequest,
    ExtractConfigPreviewResponse,
    ExtractConfigPublishResponse,
    EdiFileSummaryFilter,
    EdiFileSummaryResponse,
    EdiInvalidIdentifier,
    EdiPartnerMapping,
    EdiValidSenderReceiver,
    CreateEdiValidSenderReceiverRequest,
    Announcement,
    GetProcessingRequestsParams,
    ProcessingRequestHistoryItem,
    LogEntryUser,
    SearchLogEntriesParams,
    SearchLogEntriesResponse,
    Feature, GetAllUsersResponse,
    DistributionCenter,
    GetDistributionCentersResponse,
    SaveDistributionCenterRequest,
    GetEnabledFeaturesRequest,
    GetThoughtspotTokenRequest,
    PartnerMapping,
    SaveAnnouncementRequest,
    SavePartnerMapRow,
    DcOverride,
    DcOverrideIndicator,
    SaveDcOverridesRequest,
    MasterDataLoadRun,
    MasterDataLoadRunDetail,
    PublishEdi997ConfigRequest,
    PublishEdi997ConfigResponse,
    RefreshWebDataConnectionRequest,
    RefreshWebDataConnectionResponse,
    RegenerateExtractRequest,
    RegenerateExtractResponse,
    AssignProductsRequest,
    CreateProductGroupRequest,
    Product,
    ProductGroup,
    ProductGroupDetail,
    ProductGroupSummary,
    UserProductGroup,
    SetUserProductGroupsRequest,
    ReprocessingFile,
    ReprocessingRequest,
    ReprocessingResponse,
    Reprocess852FilesByPartnerRequest,
    Count852FilesByPartnerResponse,
    TradingPartner,
    UpdateAdminUserRequest,
    UpdateProductGroupRequest,
    UpdateFeatureFlagForUserRequest,
    UpdateFeatureFlagRequest,
    FeatureFlagHistoryParams,
    FeatureFlagHistoryResponse,
    UpdateTradingPartnerRequest,
    UpdateUserRequest,
    UpdateWebDataConnectionSqlRequest,
    UpdateWebDataConnectionThoughtSpotRequest,
    User,
    UserActivityParams,
    UserActivityResponse,
    VineMedsS3Object, VineMedsS3ObjectVersion,
    WebDataConnection,
    WebDataConnectionApiKeyResponse,
    WebDataConnectionRun,
    ZaCode,
} from "@app/_models/api";
import {environment} from "@environments/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "@app/_services/auth.service";
import {ThoughtSpotLoginResponse} from "@app/_models/thoughtspot";
import {ToastService} from "@app/_services/toast.service";

@Injectable({
    providedIn: 'root'
})
export class ApiService implements OnDestroy {
    private userSubscription: Subscription;
    user: IdentityUser | null = null;

    constructor(private http: HttpClient, private toastService: ToastService, private authService: AuthService) {
        this.userSubscription = this.authService.user().subscribe((user: IdentityUser | null) => this.user = user);
    }

    ngOnDestroy() {
        this.userSubscription.unsubscribe();
    }

    public getEnabledFeatures(body: GetEnabledFeaturesRequest): Observable<Feature[]> {
        return this.http.post<Feature[]>(`${environment.apiUrl}/admin/features/enabled`, body);
    }

    public getAnnouncements(): Observable<Announcement[]> {
        return this.http.get<Announcement[]>(`${environment.apiUrl}/admin/announcements`);
    }

    public createAnnouncement(body: SaveAnnouncementRequest): Observable<Announcement> {
        return this.http.post<Announcement>(`${environment.apiUrl}/admin/announcements`, body);
    }

    public updateAnnouncement(id: string, body: SaveAnnouncementRequest): Observable<Announcement> {
        return this.http.put<Announcement>(`${environment.apiUrl}/admin/announcements/${id}`, body);
    }

    public deleteAnnouncement(id: string): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/admin/announcements/${id}`);
    }

    public getEdiValidSenderReceivers(): Observable<EdiValidSenderReceiver[]> {
        return this.http.get<EdiValidSenderReceiver[]>(`${environment.apiUrl}/admin/edi-valid-sender-receivers`);
    }

    public createEdiValidSenderReceiver(body: CreateEdiValidSenderReceiverRequest): Observable<EdiValidSenderReceiver> {
        return this.http.post<EdiValidSenderReceiver>(`${environment.apiUrl}/admin/edi-valid-sender-receivers`, body);
    }

    public updateEdiValidSenderReceiver(id: string, isActive: boolean): Observable<EdiValidSenderReceiver> {
        return this.http.put<EdiValidSenderReceiver>(`${environment.apiUrl}/admin/edi-valid-sender-receivers/${id}`, {isActive});
    }

    public getFeatureFlags(): Observable<Feature[]> {
        return this.http.get<Feature[]>(`${environment.apiUrl}/admin/features`);
    }

    public updateFeatureFlag(body: UpdateFeatureFlagRequest): Observable<Feature> {
        return this.http.put<Feature>(`${environment.apiUrl}/admin/feature`, body);
    }

    public updateFeatureForUserFlag(body: UpdateFeatureFlagForUserRequest): Observable<Feature> {
        return this.http.put<Feature>(`${environment.apiUrl}/admin/feature/user`, body);
    }

    public getFeatureFlagHistory(code: string | null, filter: FeatureFlagHistoryParams): Observable<FeatureFlagHistoryResponse> {
        const params = new HttpParams()
            .set("page", filter.page)
            .set("includeUserChanges", filter.includeUserChanges);
        const endpoint = code === null
            ? `${environment.apiUrl}/admin/feature-flags/history`
            : `${environment.apiUrl}/admin/feature-flags/${encodeURIComponent(code)}/history`;

        return this.http.get<FeatureFlagHistoryResponse>(endpoint, {params});
    }

    public getUserActivity(cognitoUserId: string, filter: UserActivityParams): Observable<UserActivityResponse> {
        let params = new HttpParams().set("page", filter.page);
        if (filter.from) {
            params = params.set("from", filter.from);
        }
        if (filter.to) {
            params = params.set("to", filter.to);
        }

        return this.http.get<UserActivityResponse>(`${environment.apiUrl}/admin/users/${cognitoUserId}/activity`, {params});
    }

    public getCustomers(): Observable<Customer[]> {
        const endpoint = `${environment.apiUrl}/admin/customers`;
        return this.http.get<Customer[]>(endpoint);
    }

    public getAdminUsers(): Observable<AdminUser[]> {
        return this.http.get<AdminUser[]>(`${environment.apiUrl}/admin/users`);
    }

    public createAdminUser(body: CreateAdminUserRequest): Observable<AdminUser> {
        return this.http.post<AdminUser>(`${environment.apiUrl}/admin/users`, body);
    }

    public updateAdminUser(userId: string, body: UpdateAdminUserRequest): Observable<AdminUser> {
        return this.http.put<AdminUser>(`${environment.apiUrl}/admin/users/${userId}`, body);
    }

    public deleteAdminUser(userId: string): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/admin/users/${userId}`);
    }

    public getUsers(customerId: string, includeVerificationStatus: boolean = false): Observable<GetAllUsersResponse> {
        const params: Record<string, boolean> = includeVerificationStatus ? {includeVerificationStatus: true} : {};
        return this.http.get<GetAllUsersResponse>(`${environment.apiUrl}/admin/customers/${customerId!}/users`, {params});
    }

    public createUser(body: CreateUserRequest): Observable<User> {
        return this.http.post<User>(`${environment.apiUrl}/admin/customers/${body.customerId}/users`, body);
    }

    public updateUser(userId: string, body: UpdateUserRequest): Observable<User> {
        return this.http.put<User>(`${environment.apiUrl}/admin/customers/${body.customerId}/users/${userId}`, body);
    }

    public deleteUser(body: DeleteUserRequest): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/admin/customers/${body.customerId}/users/${body.userId}`);
    }

    public resendUserVerification(customerId: string, userId: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}/admin/customers/${customerId}/users/${userId}/resend-verification`, {});
    }

    getTradingPartners(customerId: string): Observable<TradingPartner[]> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners`;
        return this.http.get<TradingPartner[]>(endpoint);
    }

    public updateTradingPartner(customerId: string, tradingPartnerId: string, body: UpdateTradingPartnerRequest): Observable<TradingPartner> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}`;
        return this.http.put<TradingPartner>(endpoint, body);
    }

    getProducts(customerId: string): Observable<Product[]> {
        return this.http.get<Product[]>(`${environment.apiUrl}/admin/customers/${customerId}/products`);
    }

    getProductGroups(customerId: string): Observable<ProductGroupSummary[]> {
        return this.http.get<ProductGroupSummary[]>(`${environment.apiUrl}/admin/customers/${customerId}/productgroups`);
    }

    getProductGroup(customerId: string, productGroupId: string): Observable<ProductGroupDetail> {
        return this.http.get<ProductGroupDetail>(`${environment.apiUrl}/admin/customers/${customerId}/productgroups/${productGroupId}`);
    }

    createProductGroup(customerId: string, body: CreateProductGroupRequest): Observable<ProductGroup> {
        return this.http.post<ProductGroup>(`${environment.apiUrl}/admin/customers/${customerId}/productgroups`, body);
    }

    updateProductGroup(customerId: string, productGroupId: string, body: UpdateProductGroupRequest): Observable<ProductGroup> {
        return this.http.put<ProductGroup>(`${environment.apiUrl}/admin/customers/${customerId}/productgroups/${productGroupId}`, body);
    }

    deleteProductGroup(customerId: string, productGroupId: string): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/admin/customers/${customerId}/productgroups/${productGroupId}`);
    }

    assignProductsToGroup(customerId: string, productGroupId: string, body: AssignProductsRequest): Observable<any> {
        return this.http.post(`${environment.apiUrl}/admin/customers/${customerId}/productgroups/${productGroupId}/products`, body);
    }

    unassignProductsFromGroup(customerId: string, productGroupId: string, body: AssignProductsRequest): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/admin/customers/${customerId}/productgroups/${productGroupId}/products`, {body});
    }

    getUserProductGroups(customerId: string, userId: string): Observable<UserProductGroup[]> {
        return this.http.get<UserProductGroup[]>(`${environment.apiUrl}/admin/customers/${customerId}/users/${userId}/productgroups`);
    }

    setUserProductGroups(customerId: string, userId: string, body: SetUserProductGroupsRequest): Observable<any> {
        return this.http.put(`${environment.apiUrl}/admin/customers/${customerId}/users/${userId}/productgroups`, body);
    }

    getDistributionCenters(customerId: string, tradingParnterId: string): Observable<GetDistributionCentersResponse> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingParnterId}/distributioncenters`;
        return this.http.get<GetDistributionCentersResponse>(endpoint);
    }

    getAllDistributionCenters(customerId: string): Observable<GetDistributionCentersResponse> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/distributioncenters`;
        return this.http.get<GetDistributionCentersResponse>(endpoint);
    }

    createDistributionCenter(customerId: string, tradingPartnerId: string, body: SaveDistributionCenterRequest): Observable<DistributionCenter> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}/distributioncenters`;
        return this.http.post<DistributionCenter>(endpoint, body);
    }

    updateDistributionCenter(customerId: string, tradingPartnerId: string, distributionCenterId: string, body: SaveDistributionCenterRequest): Observable<DistributionCenter> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}/distributioncenters/${distributionCenterId}`;
        return this.http.put<DistributionCenter>(endpoint, body);
    }

    getZaCodes(): Observable<ZaCode[]> {
        const endpoint = `${environment.apiUrl}/admin/zacodes`;
        return this.http.get<ZaCode[]>(endpoint);
    }

    // --- Two-level 852 ZA maps (VMP-879) ---

    getPartnerMap(customerId: string, tradingPartnerId: string): Observable<PartnerMapping[]> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}/edi852/partner-map`;
        return this.http.get<PartnerMapping[]>(endpoint);
    }

    savePartnerMap(customerId: string, tradingPartnerId: string, rows: SavePartnerMapRow[]): Observable<unknown> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}/edi852/partner-map`;
        return this.http.put(endpoint, rows);
    }

    getDcOverrideIndicators(customerId: string, tradingPartnerId: string): Observable<DcOverrideIndicator[]> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/tradingpartners/${tradingPartnerId}/edi852/overrides`;
        return this.http.get<DcOverrideIndicator[]>(endpoint);
    }

    getAllDcOverrides(customerId: string): Observable<DcOverride[]> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/edi852/overrides`;
        return this.http.get<DcOverride[]>(endpoint);
    }

    saveDcOverrides(customerId: string, body: SaveDcOverridesRequest): Observable<unknown> {
        const endpoint = `${environment.apiUrl}/admin/customers/${customerId}/edi852/overrides`;
        return this.http.put(endpoint, body);
    }

    deleteDcOverride(customerId: string, distributionCenterId: string, zaCode?: string): Observable<unknown> {
        let endpoint = `${environment.apiUrl}/admin/customers/${customerId}/edi852/overrides/${distributionCenterId}`;
        if (zaCode) endpoint += `?zaCode=${encodeURIComponent(zaCode)}`;
        return this.http.delete(endpoint);
    }

    getProcessingRequests(filter: GetProcessingRequestsParams): Observable<ProcessingRequestHistoryItem[]> {
        let params = new HttpParams();

        for (const [key, value] of Object.entries(filter)) {
            if (value) {
                params = params.set(key, value);
            }
        }

        return this.http.get<ProcessingRequestHistoryItem[]>(`${environment.apiUrl}/admin/processing/requests`, {params});
    }

    searchLogEntries(filter: SearchLogEntriesParams): Observable<SearchLogEntriesResponse> {
        let params = new HttpParams();

        for (const [key, value] of Object.entries(filter)) {
            if (value) {
                params = params.set(key, value);
            }
        }

        return this.http.get<SearchLogEntriesResponse>(`${environment.apiUrl}/admin/log-entries`, {params});
    }

    getLogEntryTables(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}/admin/log-entries/tables`);
    }

    getLogEntryUsers(): Observable<LogEntryUser[]> {
        return this.http.get<LogEntryUser[]>(`${environment.apiUrl}/admin/log-entries/users`);
    }

    getEdi852FileSummary(filter: EdiFileSummaryFilter): Observable<EdiFileSummaryResponse[]> {
        let params = new HttpParams();

        if (filter.customerId) {
            params = params.set('customerId', filter.customerId);
        }
        if (filter.tradingPartnerName) {
            params = params.set('tradingPartnerName', filter.tradingPartnerName);
        }
        if (filter.fileName) {
            params = params.set('fileName', filter.fileName);
        }
        if (filter.reportingStartDate) {
            params = params.set('reportingStartDate', filter.reportingStartDate);
        }
        if (filter.reportingEndDate) {
            params = params.set('reportingEndDate', filter.reportingEndDate);
        }
        if (filter.loadStartDate) {
            params = params.set('loadStartDate', filter.loadStartDate);
        }
        if (filter.loadEndDate) {
            params = params.set('loadEndDate', filter.loadEndDate);
        }
        if (filter.maintenanceIssuesOnly) {
            params = params.set('maintenanceIssuesOnly', filter.maintenanceIssuesOnly);
        }

        const url = `${environment.apiUrl}/admin/processing/852`;
        return this.http.get<EdiFileSummaryResponse[]>(url, { params });
    }

    getEdi867FileSummary(filter: EdiFileSummaryFilter): Observable<EdiFileSummaryResponse[]> {
        let params = new HttpParams();

        if (filter.customerId) {
            params = params.set('customerId', filter.customerId);
        }
        if (filter.tradingPartnerName) {
            params = params.set('tradingPartnerName', filter.tradingPartnerName);
        }
        if (filter.fileName) {
            params = params.set('fileName', filter.fileName);
        }
        if (filter.reportingStartDate) {
            params = params.set('reportingStartDate', filter.reportingStartDate);
        }
        if (filter.reportingEndDate) {
            params = params.set('reportingEndDate', filter.reportingEndDate);
        }
        if (filter.loadStartDate) {
            params = params.set('loadStartDate', filter.loadStartDate);
        }
        if (filter.loadEndDate) {
            params = params.set('loadEndDate', filter.loadEndDate);
        }
        if (filter.maintenanceIssuesOnly) {
            params = params.set('maintenanceIssuesOnly', filter.maintenanceIssuesOnly);
        }

        const url = `${environment.apiUrl}/admin/processing/867`;
        return this.http.get<EdiFileSummaryResponse[]>(url, { params });
    }

    getEdiPartnerMappings(): Observable<EdiPartnerMapping[]> {
        const url = `${environment.apiUrl}/admin/processing/edi-partner-mappings`;
        return this.http.get<EdiPartnerMapping[]>(url);
    }

    reprocess852Files(files: ReprocessingFile[]): Observable<ReprocessingResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/852/reprocess`;
        const body: ReprocessingRequest = { files };
        return this.http.post<ReprocessingResponse>(endpoint, body);
    }

    count852FilesByPartner(customerId: string, tradingPartnerId: string): Observable<Count852FilesByPartnerResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/852/reprocess-by-partner/count`;
        const params = new HttpParams().set('customerId', customerId).set('tradingPartnerId', tradingPartnerId);
        return this.http.get<Count852FilesByPartnerResponse>(endpoint, {params});
    }

    reprocess852FilesByPartner(customerId: string, tradingPartnerId: string): Observable<ReprocessingResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/852/reprocess-by-partner`;
        const body: Reprocess852FilesByPartnerRequest = {customerId, tradingPartnerId};
        return this.http.post<ReprocessingResponse>(endpoint, body);
    }

    reprocess867Files(files: ReprocessingFile[]): Observable<ReprocessingResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/867/reprocess`;
        const body: ReprocessingRequest = { files };
        return this.http.post<ReprocessingResponse>(endpoint, body);
    }

    backout852Files(request: BackoutRequest[]): Observable<BackoutResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/852/backout`;
        return this.http.post<BackoutResponse>(endpoint, request);
    }

    backout867Files(request: BackoutRequest[]): Observable<BackoutResponse> {
        const endpoint = `${environment.apiUrl}/admin/processing/867/backout`;
        return this.http.post<BackoutResponse>(endpoint, request);
    }

    getEdi852InvalidIdentifiers(customerId: string): Observable<EdiInvalidIdentifier[]> {
        const url = `${environment.apiUrl}/admin/processing/852/invalid-identifiers`;
        let params = new HttpParams();
        params = params.set('customerId', customerId);
        return this.http.get<EdiInvalidIdentifier[]>(url, { params });
    }

    getEdi867InvalidIdentifiers(customerId: string): Observable<EdiInvalidIdentifier[]> {
        const url = `${environment.apiUrl}/admin/processing/867/invalid-identifiers`;
        let params = new HttpParams();
        params = params.set('customerId', customerId);
        return this.http.get<EdiInvalidIdentifier[]>(url, { params });
    }

    getAllExtracts(): Observable<VineMedsS3Object[]> {
        const url = `${environment.apiUrl}/admin/extracts`;
        return this.http.get<VineMedsS3Object[]>(url);
    }

    getExtractsByCustomerCode(customerCode: string = ''): Observable<VineMedsS3ObjectVersion[]> {
        let routeSuffix = '';

        if (customerCode) {
            routeSuffix = `/${customerCode}`;
        }
        const url = `${environment.apiUrl}/admin/extracts${routeSuffix}`;
        return this.http.get<VineMedsS3ObjectVersion[]>(url);
    }

    getExtractKeysByCustomerCode(customerCode: string) : Observable<string[]> {
        const endpoint = `${environment.apiUrl}/admin/extracts/${customerCode}/keys`;
        return this.http.get<string[]>(endpoint);
    }

    regenerateExtractByKey(request: RegenerateExtractRequest) : Observable<RegenerateExtractResponse> {
        const endpoint = `${environment.apiUrl}/admin/extract/regenerate`;
        return this.http.post<RegenerateExtractResponse>(endpoint, request);
    }

    async getThoughtspotToken(body: GetThoughtspotTokenRequest): Promise<ThoughtSpotLoginResponse> {
        const request$ = this.http.post<Promise<ThoughtSpotLoginResponse>>(`${environment.apiUrl}/admin/thoughtspot/auth-token`, body).pipe(take(1));
        return await lastValueFrom<Promise<ThoughtSpotLoginResponse>>(request$);
    }

    handleDownloadFile(endpoint: string): void {
        // Chain with feature flags to refresh an expired login token first
        this.getFeatureFlags().subscribe({
            next: () => this.navigateToDownload(endpoint),
            error: () => this.toastService.showDanger("Download failed", "Please try again.")
        });
    }

    private navigateToDownload(endpoint: string): void {
        // simple wrapper for cleaner unit testing
        window.location.assign(endpoint);
    }

    getEdi997Configs(): Observable<Edi997Config[]> {
        const url = `${environment.apiUrl}/admin/edi997/configs`;
        return this.http.get<Edi997Config[]>(url);
    }

    getEdi997ConfigConnectors(): Observable<Edi997ConfigConnector[]> {
        const url = `${environment.apiUrl}/admin/edi997/connectors`;
        return this.http.get<Edi997ConfigConnector[]>(url);
    }

    getEdi997ConfigDetails(id: string): Observable<Edi997ConfigDetails> {
        const url = `${environment.apiUrl}/admin/edi997/config/${id}`;
        return this.http.get<Edi997ConfigDetails>(url);
    }

    createEdi997Config(request: Edi997CreateRequest): Observable<Edi997Config> {
        const url = `${environment.apiUrl}/admin/edi997/config`;
        return this.http.post<Edi997Config>(url, request);
    }

    publishEdi997Config(request: PublishEdi997ConfigRequest): Observable<PublishEdi997ConfigResponse> {
        const url = `${environment.apiUrl}/admin/edi997/config/publish/${request.id}`;
        return this.http.put<PublishEdi997ConfigResponse>(url, request);
    }

    getExtractConfigEntries(): Observable<ExtractConfigEntry[]> {
        const url = `${environment.apiUrl}/admin/extract-config-entries`;
        return this.http.get<ExtractConfigEntry[]>(url);
    }

    getExtractConfigEntry(id: string): Observable<ExtractConfigEntryDetails> {
        const url = `${environment.apiUrl}/admin/extract-config-entries/${id}`;
        return this.http.get<ExtractConfigEntryDetails>(url);
    }

    createExtractConfigEntry(request: ExtractConfigEntryRequest): Observable<ExtractConfigEntryDetails> {
        const url = `${environment.apiUrl}/admin/extract-config-entries`;
        return this.http.post<ExtractConfigEntryDetails>(url, request);
    }

    updateExtractConfigEntry(id: string, request: ExtractConfigEntryRequest): Observable<ExtractConfigEntryDetails> {
        const url = `${environment.apiUrl}/admin/extract-config-entries/${id}`;
        return this.http.put<ExtractConfigEntryDetails>(url, request);
    }

    deleteExtractConfigEntry(id: string): Observable<void> {
        const url = `${environment.apiUrl}/admin/extract-config-entries/${id}`;
        return this.http.delete<void>(url);
    }

    previewExtractConfig(): Observable<ExtractConfigPreviewResponse> {
        const url = `${environment.apiUrl}/admin/extract-config-entries/preview`;
        return this.http.get<ExtractConfigPreviewResponse>(url);
    }

    publishExtractConfig(): Observable<ExtractConfigPublishResponse> {
        const url = `${environment.apiUrl}/admin/extract-config-entries/publish`;
        return this.http.post<ExtractConfigPublishResponse>(url, {});
    }

    getWebDataConnections(customerId: string): Observable<WebDataConnection[]> {
        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnections`;
        return this.http.get<WebDataConnection[]>(url);
    }

    getWebDataConnectionRuns(customerId: string, webDataConnectionId: string): Observable<WebDataConnectionRun[]> {
        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnection/${webDataConnectionId}/runs`;
        return this.http.get<WebDataConnectionRun[]>(url);
    }

    createWebDataConnectionSql(
        customerId: string, request: CreateWebDataConnectionSqlRequest): Observable<WebDataConnection> {

        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnection/sql`;
        return this.http.post<WebDataConnection>(url, request);
    }

    updateWebDataConnectionSql(
        customerId: string, webDataConnectionId: string,
        request: UpdateWebDataConnectionSqlRequest): Observable<WebDataConnection> {

        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnection/sql/${webDataConnectionId}`;
        return this.http.put<WebDataConnection>(url, request);
    }

    createWebDataConnectionThoughtSpot(
        customerId: string, request: CreateWebDataConnectionThoughtSpotRequest): Observable<WebDataConnection> {

        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnection/thoughtspot`;
        return this.http.post<WebDataConnection>(url, request);
    }

    updateWebDataConnectionThoughtSpot(
        customerId: string, webDataConnectionId: string,
        request: UpdateWebDataConnectionThoughtSpotRequest): Observable<WebDataConnection> {

        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnection/thoughtspot/${webDataConnectionId}`;
        return this.http.put<WebDataConnection>(url, request);
    }

    refreshWebDataConnection(request: RefreshWebDataConnectionRequest): Observable<RefreshWebDataConnectionResponse> {
        const url = `${environment.apiUrl}/admin/customers/${request.customerId}/webDataConnection/refresh`;
        return this.http.post<RefreshWebDataConnectionResponse>(url, request);
    }

    deleteWebDataConnection(request: DeleteWebDataConnectionRequest): Observable<DeleteWebDataConnectionResponse> {
        const url = `${environment.apiUrl}/admin/customers/${request.customerId}/webDataConnection/${request.webDataConnectionId}`;
        return this.http.delete<DeleteWebDataConnectionResponse>(url);
    }

    getApiKey(customerId: string): Observable<WebDataConnectionApiKeyResponse> {
        const url = `${environment.apiUrl}/admin/customers/${customerId}/webDataConnections/apiKey`;
        return this.http.get<WebDataConnectionApiKeyResponse>(url);
    }

    // --- Master Data Loads ---------------------------------------------------

    getMasterDataLoads(customerId?: string): Observable<MasterDataLoadRun[]> {
        let params = new HttpParams();
        if (customerId) {
            params = params.set('customerId', customerId);
        }
        return this.http.get<MasterDataLoadRun[]>(`${environment.apiUrl}/admin/master-data-loads`, {params});
    }

    getMasterDataLoad(loadId: string): Observable<MasterDataLoadRunDetail> {
        return this.http.get<MasterDataLoadRunDetail>(`${environment.apiUrl}/admin/master-data-loads/${loadId}`);
    }

    // Multipart upload — one form part per File Kind. Let the browser set the
    // multipart/form-data Content-Type (with boundary); do not set it manually.
    createMasterDataLoad(customerId: string, formData: FormData): Observable<CreateMasterDataLoadResponse> {
        const url = `${environment.apiUrl}/admin/customers/${customerId}/master-data-loads`;
        return this.http.post<CreateMasterDataLoadResponse>(url, formData);
    }
}
